import { customGetAPI } from "services/utils/CustomAPI";
import { defaultAllSelection } from "services/constants/selections";
import {
  canUseCompanyContext,
  provisioningCacheKey,
  provisioningModule,
  scopedProvisioning,
} from "services/utils/companyAccess";
import { normalize, searchEntries } from "./searchRegistry";

// Data for the top-bar search. The backend search APIs in the spec
// (GET /api/search, GET/POST /api/search/recent) don't exist yet, so this
// file does their work in the browser:
// - pages, settings and actions are searched from searchRegistry.js, built
//   from each product's provisioning payload;
// - employees come from the People employee list (list/employee);
// - Recent is kept in this browser per user.
// When the APIs arrive, only this file needs to change.

const CACHE_TTL_MS = 5 * 60 * 1000;
export const GROUP_LIMIT = 5;
export const SEE_ALL_LIMIT = 20;
export const RECENT_SHOWN = 6;
const RECENT_KEPT = 20;

// ---- Permissions: one provisioning payload per product ----

const accessCache = new Map();

// The provisioning payload for one product (same request the sidebar
// makes), cached for 5 minutes. Resolves null when it can't be loaded, so
// that product's pages stay out of the results.
export const loadProductAccess = ({ domain, userId, userType, companyId, companies, productName }) => {
  if (!canUseCompanyContext(companyId, userType, companies)) {
    return Promise.resolve({ product_name: productName, company_id: companyId, module_details: [] });
  }
  const key = provisioningCacheKey(domain, userId, companyId, productName);
  const cached = accessCache.get(key);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.promise;
  const promise = customGetAPI(
    `${domain}product/access/provisioning/?company_id=${companyId}&module=${encodeURIComponent(provisioningModule(companyId, productName))}`,
  )
    .then((response) =>
      response?.ok ? scopedProvisioning(response.responseDetails, companyId, productName) : null,
    )
    .catch(() => null)
    .then((access) => {
      if (!access) accessCache.delete(key);
      return access;
    });
  accessCache.set(key, { at: Date.now(), promise });
  return promise;
};

// ---- Employees ----

const employeeCache = new Map();

const loadEmployees = ({ domain, companyId }) => {
  const key = `${domain}|${companyId}`;
  const cached = employeeCache.get(key);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.promise;
  const promise = fetch(
    `${domain}list/employee?company_id=${companyId}&department_id=${defaultAllSelection}&status=Active`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `token ${localStorage.getItem("token")}`,
      },
    },
  )
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error("REQUEST_FAILED"))))
    .then((data) => (Array.isArray(data) ? data : data?.data || data?.results || []))
    .catch(() => {
      employeeCache.delete(key);
      return [];
    });
  employeeCache.set(key, { at: Date.now(), promise });
  return promise;
};

const digitsOnly = (text) => String(text ?? "").replace(/\D/g, "");
const employeeCode = (employee) => employee?.employee_id || employee?.user_code || "";
const employeeMobile = (employee) =>
  digitsOnly(
    employee?.phonenumber || employee?.phone_number || employee?.mobile_number ||
    employee?.mobile || employee?.phone || "",
  ).slice(-10);

// Code and mobile match from the start; a name matches when one of its
// words starts with the text. Code matches rank above name matches.
const employeeRank = (employee, query) => {
  const needle = normalize(query);
  if (!needle) return null;
  if (normalize(employeeCode(employee)).startsWith(needle)) return 1;
  const queryDigits = digitsOnly(query);
  if (/^[\d\s+()-]+$/.test(query.trim()) && queryDigits.length >= 3 && employeeMobile(employee).startsWith(queryDigits)) {
    return 2;
  }
  const name = normalize(employee?.user_name);
  if (name.startsWith(needle) || name.split(" ").some((word, index, words) => words.slice(index).join(" ").startsWith(needle))) {
    return 3;
  }
  return null;
};

// Shows name, code and department only; never pay values (spec 5).
const toEmployeeResult = (employee) => ({
  id: `employee:${employee.user_id}`,
  type: "employee",
  title: employee.user_name || "Unnamed employee",
  path: [employeeCode(employee), employee.department].filter(Boolean).join(" · "),
  route: `/admin/hrzone/employees?employee=${encodeURIComponent(employee.user_id)}`,
  initials: String(employee.user_name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join(""),
});

const highlightIn = (text, query) => {
  const at = String(text).toLowerCase().indexOf(String(query).trim().toLowerCase());
  return at >= 0 ? [[at, at + query.trim().length]] : [];
};

// ---- Search (stands in for GET /api/search?q=&limit=) ----

// Results grouped as the API will return them. `more` says which groups
// had more matches than `limit`. Rejects with an AbortError when `signal`
// is aborted, so only the latest search's results are shown.
export const search = async ({ query, registry, recentIds, employeeSource, limit = GROUP_LIMIT, signal }) => {
  const groups = {
    pages: searchEntries(registry.pages, query, recentIds),
    settings: searchEntries(registry.settings, query, recentIds),
    actions: searchEntries(registry.actions, query, recentIds),
    employees: [],
  };
  if (employeeSource) {
    const employees = await loadEmployees(employeeSource);
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    groups.employees = employees
      .map((employee) => ({ employee, rank: employeeRank(employee, query) }))
      .filter((item) => item.rank != null && item.employee?.user_id != null)
      .sort((a, b) => a.rank - b.rank || String(a.employee.user_name).localeCompare(String(b.employee.user_name)))
      .map(({ employee, rank }) => {
        const result = toEmployeeResult(employee);
        return {
          ...result,
          highlight: rank === 3 ? highlightIn(result.title, query) : [],
          pathHighlight: rank === 1 ? highlightIn(result.path, query) : [],
        };
      });
  }
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  const more = {};
  Object.keys(groups).forEach((key) => {
    more[key] = groups[key].length > limit;
    groups[key] = groups[key].slice(0, limit);
  });
  return { ...groups, more };
};

// ---- Recent (stands in for GET/POST /api/search/recent) ----

const recentKey = (domain, userId) => `globalSearchRecent:${domain}:${userId}`;

// Recently opened pages/settings, newest first (only ids, path and title).
export const getRecent = ({ domain, userId }) => {
  if (!userId) return [];
  try {
    const list = JSON.parse(localStorage.getItem(recentKey(domain, userId)) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

// Moves `entry` to the top of Recent; keeps the last 20.
export const addRecent = ({ domain, userId }, entry) => {
  if (!userId || !entry?.id || !["page", "setting"].includes(entry.type)) return;
  const item = { id: entry.id, type: entry.type };
  const next = [item, ...getRecent({ domain, userId }).filter((recent) => recent.id !== entry.id)].slice(0, RECENT_KEPT);
  try {
    localStorage.setItem(recentKey(domain, userId), JSON.stringify(next));
  } catch {
    // Storage blocked: Recent just isn't remembered.
  }
};
